import { Money } from "@redotech/money";
import { Currency } from "@redotech/money/currencies";
import { RateSource } from "@redotech/money/rate-source";
import { Rate, RateRef } from "./rate";

/**
 * Source-aware FX client facade. Each method takes an explicit `source` because
 * rates from different sources are not interchangeable. Async because rates are
 * served from a cache the FX service keeps warm.
 */
export interface FxClient {
  /** Cold source → throws. */
  getRate(base: Currency, quote: Currency, source: RateSource): Promise<Rate>;
  /** Presentment-only — the result is not struck. */
  convert(
    money: Money,
    toCurrency: Currency,
    source: RateSource,
  ): Promise<Money>;
  /**
   * Current rate per ref on subscribe, then deltas, over one merged stream. Each
   * Rate self-identifies, so consumers route without tracking subscription order.
   * Single-pair callers pass a one-element array — there is no per-pair method.
   */
  watchRates(
    refs: readonly RateRef[],
    signal?: AbortSignal,
  ): AsyncIterable<Rate>;
  // No getRateAsOf / rate history by design: a frozen value carries its `struck`
  // snapshot, so audits and refunds reconvert from it rather than a historical rate.
}

export class RateUnavailableError extends Error {
  override readonly name = "RateUnavailableError";
  constructor(
    readonly base: Currency,
    readonly quote: Currency,
    readonly requested: RateSource,
  ) {
    super(`No ${requested} rate available for ${base}/${quote}`);
  }
}
